import NepaliDateConverter from 'nepali-date-converter';

/**
 * Convert English date to Nepali date
 * @param {string|Date} englishDate - English date (YYYY-MM-DD format or Date object)
 * @returns {string} Nepali date in YYYY-MM-DD format
 */
export const englishToNepaliDate = (englishDate) => {
  try {
    const date = typeof englishDate === 'string' ? new Date(englishDate) : englishDate;
    const nepaliDateObj = new NepaliDateConverter(date);
    const nepaliDate = nepaliDateObj.getBS();
    
    // Format as YYYY-MM-DD
    const year = nepaliDate.year;
    const month = (nepaliDate.month + 1).toString().padStart(2, '0');
    const day = nepaliDate.date.toString().padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error converting English to Nepali date:', error);
    return '';
  }
};

/**
 * Convert Nepali date to English date
 * @param {string} nepaliDate - Nepali date in YYYY-MM-DD format
 * @returns {string} English date in YYYY-MM-DD format
 */
export const nepaliToEnglishDate = (nepaliDate) => {
  try {
    if (!nepaliDate || typeof nepaliDate !== 'string') {
      return '';
    }
    
    const parts = nepaliDate.split('-');
    if (parts.length !== 3) {
      return '';
    }
    
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const day = parseInt(parts[2]);
    
    // Create Nepali date object
    const nepaliDateObj = new NepaliDateConverter(year, month, day);
    const englishDate = nepaliDateObj.getAD();
    
    // Format as YYYY-MM-DD
    const engYear = englishDate.year;
    const engMonth = (englishDate.month + 1).toString().padStart(2, '0');
    const engDay = englishDate.date.toString().padStart(2, '0');
    
    return `${engYear}-${engMonth}-${engDay}`;
  } catch (error) {
    console.error('Error converting Nepali to English date:', error);
    return '';
  }
};

/**
 * Format date for display in input field
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date for input field
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  // Ensure the date is in YYYY-MM-DD format for input fields
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Get current date in both English and Nepali formats
 * @returns {object} Object with english and nepali date strings
 */
export const getCurrentDates = () => {
  const today = new Date();
  const englishDate = formatDateForInput(today);
  const nepaliDate = englishToNepaliDate(today);
  
  return {
    english: englishDate,
    nepali: nepaliDate
  };
};

/**
 * Validate date string format (YYYY-MM-DD)
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid format
 */
export const isValidDateFormat = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return false;
  
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateRegex.test(dateString);
};

/**
 * Get English date range for a Nepali month
 * @param {number} nepYear - Nepali year
 * @param {number} nepMonth - Nepali month (1-12)
 * @returns {object} Object with start and end English dates for the Nepali month
 */
export const getEnglishDateRangeForNepaliMonth = (nepYear, nepMonth) => {
  try {
    // Convert first day of Nepali month to English
    const nepaliStartDate = new NepaliDateConverter(nepYear, nepMonth - 1, 1);
    const englishStartDate = nepaliStartDate.getAD();

    // Compute first day of next Nepali month, then subtract 1 day to get precise end
    const nextNepYear = nepMonth === 12 ? nepYear + 1 : nepYear;
    const nextNepMonth = nepMonth === 12 ? 1 : nepMonth + 1;
    const nepaliNextMonthStart = new NepaliDateConverter(nextNepYear, nextNepMonth - 1, 1);
    const englishNextMonthStart = nepaliNextMonthStart.getAD();

    // End is the day before next month starts
    const endDateObj = new Date(englishNextMonthStart.year, englishNextMonthStart.month, englishNextMonthStart.date);
    endDateObj.setDate(endDateObj.getDate() - 1);

    const startDateObj = new Date(englishStartDate.year, englishStartDate.month, englishStartDate.date);
    const diffMs = endDateObj - startDateObj;
    const daysInMonth = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;

    return {
      start: {
        year: englishStartDate.year,
        month: englishStartDate.month + 1,
        day: englishStartDate.date
      },
      end: {
        year: endDateObj.getFullYear(),
        month: endDateObj.getMonth() + 1,
        day: endDateObj.getDate()
      },
      daysInMonth
    };
  } catch (error) {
    console.error('Error getting English date range for Nepali month:', error);
    return null;
  }
};

/**
 * Get the primary English month for a Nepali month
 * Used for API calls where we need to select one English month
 * @param {number} nepYear - Nepali year
 * @param {number} nepMonth - Nepali month (1-12)
 * @returns {object} Object with year and month for API call
 */
export const getPrimaryEnglishMonthForNepaliMonth = (nepYear, nepMonth) => {
  try {
    const dateRange = getEnglishDateRangeForNepaliMonth(nepYear, nepMonth);
    if (!dateRange) {
      // Fallback: use first day conversion
      const nepaliDateObj = new NepaliDateConverter(nepYear, nepMonth - 1, 1);
      const englishDateObj = nepaliDateObj.getAD();
      return {
        year: englishDateObj.year,
        month: englishDateObj.month + 1
      };
    }
    
    // For most Nepali months, the majority of days fall in one English month
    // We'll use the month that contains the majority of the Nepali month
    const startMonth = dateRange.start.month;
    const endMonth = dateRange.end.month;
    
    if (startMonth === endMonth) {
      return {
        year: dateRange.start.year,
        month: startMonth
      };
    } else {
      // If the Nepali month spans two English months, use the month with more days
      // Calculate days in each English month
      const daysInStartMonth = dateRange.daysInMonth - (dateRange.start.day - 1);
      const daysInEndMonth = dateRange.end.day;
      
      return daysInStartMonth >= daysInEndMonth ? 
        { year: dateRange.start.year, month: startMonth } :
        { year: dateRange.end.year, month: endMonth };
    }
  } catch (error) {
    console.error('Error getting primary English month for Nepali month:', error);
    
    // Ultimate fallback: use first day conversion
    const nepaliDateObj = new NepaliDateConverter(nepYear, nepMonth - 1, 1);
    const englishDateObj = nepaliDateObj.getAD();
    return {
      year: englishDateObj.year,
      month: englishDateObj.month + 1
    };
  }
};
